import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { IconChevron } from '../../components/icons/WizardIcons';
import {
  listCaseRequestSubsidiaryTasks,
  type CaseRequestSubsidiaryTaskRow,
} from '../../services/caseRequestSubsidiaryTasks';
import { formatAuditDateTime, parseToIsoDate } from '../../utils/dateFormat';
import { isCaseRequestUuid } from './taskCaseUpdateHelpers';

const EMPTY = '—';

function statusPillClass(status: string): string {
  const s = status.trim().toLowerCase();
  if (s.includes('complete') || s.includes('closed') || s.includes('done')) return 'pill p-com';
  if (s.includes('cancel')) return 'pill p-urg-h';
  if (s.includes('pending') || s.includes('not started')) return 'pill p-pend';
  if (s.includes('progress') || s.includes('started') || s.includes('review')) return 'pill p-ip';
  return 'pill p-aw';
}

function formatSubtaskDate(value: string | null | undefined): string {
  if (!value?.trim()) return EMPTY;
  const iso = parseToIsoDate(value);
  if (iso) return dayjs(iso).format('DD MMM YYYY');
  const formatted = formatAuditDateTime(value);
  return formatted || value.trim();
}

function formatSubtaskDateTime(value: string | null | undefined): string {
  if (!value?.trim()) return EMPTY;
  return formatAuditDateTime(value) || value.trim();
}

type Props = {
  caseRequestId: string;
};

export default function TaskCaseMinutesSubTasksSection({ caseRequestId }: Props) {
  const { t } = useTranslation();
  const canLoad = isCaseRequestUuid(caseRequestId);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [tasks, setTasks] = useState<CaseRequestSubsidiaryTaskRow[]>([]);

  useEffect(() => {
    if (!canLoad) return;
    const controller = new AbortController();

    setLoading(true);
    setLoadError('');
    void listCaseRequestSubsidiaryTasks({ caseRequestId, signal: controller.signal })
      .then((res) => {
        if (controller.signal.aborted) return;
        setLoading(false);
        if (!res.success) {
          setLoadError(res.message ?? 'Failed to load subsidiary tasks.');
          return;
        }
        setTasks(res.data.tasks);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setLoading(false);
          setLoadError('Failed to load subsidiary tasks.');
        }
      });

    return () => controller.abort();
  }, [canLoad, caseRequestId]);

  if (!canLoad) return null;

  const recordLabel = tasks.length === 1 ? 'record' : 'records';

  return (
    <div className="ccr-sec tcd-minutes-subtasks-sec">
      <button
        type="button"
        className="ccr-sh tcd-minutes-subtasks-sh"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="tcd-minutes-subtasks-sh-title">
          <span className="ccr-sh-t">Subsidiary Task Details</span>
          {!loading && !loadError ? (
            <span className="tcd-minutes-subtasks-count">
              {tasks.length} {recordLabel}
            </span>
          ) : null}
        </span>
        <span className="ccr-sh-ic">
          <IconChevron expanded={open} />
        </span>
      </button>
      <div className={`ccr-sb tcd-minutes-subtasks-body${open ? '' : ' is-collapsed'}`}>
        {loading ? (
          <p className="tcd-minutes-subtasks-msg">Loading subsidiary tasks…</p>
        ) : loadError ? (
          <p className="tcd-minutes-subtasks-msg is-error" role="alert">
            {loadError}
          </p>
        ) : (
          <div className="tw tcd-minutes-subtasks-tbl-wrap">
            <table className="tcd-minutes-subtasks-tbl">
              <thead>
                <tr>
                  <th>Subsidiary Task</th>
                  <th>Description</th>
                  <th>Assigned To</th>
                  <th>Due Date</th>
                  <th>Workflow Stage</th>
                  <th>Created At</th>
                  <th>Completed At / Cancelled At</th>
                  <th>Comment</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {tasks.length ? (
                  tasks.map((row, idx) => (
                    <tr key={`${row.subsidiaryTaskId ?? row.subsidiaryTask}-${row.createdAt}-${idx}`}>
                      <td className="col-subtask">{row.subsidiaryTask || EMPTY}</td>
                      <td className="col-desc">{row.description || EMPTY}</td>
                      <td>{row.assignedTo || EMPTY}</td>
                      <td>{row.dueDate ? formatSubtaskDate(row.dueDate) : EMPTY}</td>
                      <td>
                        {row.workflowStage ? (
                          <span className="pill p-aw">{row.workflowStage}</span>
                        ) : (
                          EMPTY
                        )}
                      </td>
                      <td>{row.createdAt ? formatSubtaskDateTime(row.createdAt) : EMPTY}</td>
                      <td>
                        {row.completedAt
                          ? formatSubtaskDateTime(row.completedAt)
                          : row.cancelledAt
                            ? formatSubtaskDateTime(row.cancelledAt)
                            : EMPTY}
                      </td>
                      <td className="col-comment">{row.comment?.trim() || EMPTY}</td>
                      <td className="col-status">
                        {row.status ? (
                          <span className={statusPillClass(row.status)}>{row.status}</span>
                        ) : (
                          EMPTY
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className="tcd-minutes-subtasks-empty">
                      {t('common.noResultsFound')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
